/**
 * ZEN ERP — Animasyon Demo Sayfası (29.10.2026)
 *
 * GSAP + Lenis + React Bits araçlarının programa neler katabileceğini
 * YERELDE göstermek için hazırlanmış vitrin sayfasıdır.
 *
 * Kurulan araçlar:
 *   - gsap 3.15 + @gsap/react (useGSAP) + SplitText + ScrollTrigger  [100% ücretsiz]
 *   - lenis 1.3 (pürüzsüz kaydırma)
 *   - React Bits: CountUp, SpringCheck (motion ile) [MIT + Commons Clause]
 *
 * GSAP kuralları: resmi greensock/gsap-skills becerilerine uygun yazıldı
 * (useGSAP + scope, gsap.matchMedia ile prefers-reduced-motion, plugin kayıt).
 */
import React, { useContext, useRef, useState } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { SplitText } from "gsap/SplitText";
import Lenis from "lenis";

import CountUp from "../components/anim/CountUp";
import SpringCheck from "../components/anim/SpringCheck";
import { UICtx } from "../appContexts";

gsap.registerPlugin(useGSAP, ScrollTrigger, SplitText);

// ── Demo verileri (gerçek veri değil) ────────────────────────────────────────
const STATS = [
  { label: "Aylık Satış", value: 26282.89, prefix: "€", decimals: 2, separator: ".", note: "dashboard sayaç kartı" },
  { label: "Açık Sipariş", value: 570, suffix: " adet", note: "stok/sipariş sayaçları" },
  { label: "Müşteri", value: 76, note: "cari kart başlıkları" },
  { label: "Tahsilat", value: 447, note: "finans özetleri" },
];

const REVEALS = [
  { title: "Rapor Sayfaları", desc: "Bölümler kaydırdıkça yumuşakça belirir — uzun raporlar göz yormaz." },
  { title: "Teklif / Sipariş Detayları", desc: "Kartlar sırayla açılır; göz doğal olarak sırayı takip eder." },
  { title: "Müşteri Portalı", desc: "Bayi portalı ve paylaşılan teklif sayfalarında profesyonel ilk izlenim." },
];

const ADDS = [
  { title: "Dashboard", desc: "Tutarların animasyonlu sayımı + kartların sırayla belirmesi (CountUp + GSAP stagger)." },
  { title: "Giriş Ekranı", desc: "Başlık/logo SplitText ile canlanır, arka plan yavaş hareket eder (GSAP)." },
  { title: "Onay Butonları", desc: "Sipariş onayı, silme onayı gibi kritik adımlarda kaydır-onayla (SlideCommit tarzı)." },
  { title: "Pürüzsüz Kaydırma", desc: "Uzun listelerde ipeksi kaydırma (Lenis) — raporlar ve ayarlar sayfası." },
  { title: "Ayarlar / Formlar", desc: "Onay kutuları ve anahtarlar canlanır (SpringCheck) — kullanıcının gözü takip eder." },
  { title: "Vitrin Sayfaları", desc: "Bayi portalı, teklif paylaşımı, tanıtım sayfaları — GSAP ScrollTrigger şöleni." },
];

export default function AnimasyonDemoPage() {
  const { tr } = useContext(UICtx);
  const pageRef = useRef(null);
  const lenisRef = useRef(null);
  const [smoothOn, setSmoothOn] = useState(true);
  const [checks, setChecks] = useState([true, false, false]);

  // ── Lenis: sayfanın kendi kaydırma kabına bağlı pürüzsüz kaydırma ──────────
  useGSAP(() => {
    const el = pageRef.current;
    if (!el) return;
    const lenis = new Lenis({ wrapper: el, content: el.firstElementChild, autoRaf: true });
    lenis.on("scroll", ScrollTrigger.update);
    lenisRef.current = lenis;
    return () => {
      lenis.destroy();
      lenisRef.current = null;
    };
  }, { scope: pageRef });

  const toggleSmooth = () => {
    const next = !smoothOn;
    setSmoothOn(next);
    if (lenisRef.current) {
      if (next) lenisRef.current.start();
      else lenisRef.current.stop();
    }
  };

  // ── GSAP animasyonları (resmi kalıp: useGSAP + scope + matchMedia) ─────────
  useGSAP(() => {
    const mm = gsap.matchMedia();
    // Erişilebilirlik: hareket azaltma tercihi olanlarda animasyon yok
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      // 1) Başlık — SplitText ile harf harf giriş
      const split = SplitText.create("[data-anim='hero-title']", { type: "chars,words" });
      gsap.from(split.chars, {
        yPercent: 110, opacity: 0, duration: 0.7, ease: "power3.out", stagger: 0.025,
      });
      gsap.from("[data-anim='hero-sub']", { y: 24, opacity: 0, duration: 0.8, delay: 0.35, ease: "power2.out" });

      // 2) Dashboard kartları — sırayla belirme (stagger)
      gsap.from("[data-anim='stat-card']", {
        y: 32, opacity: 0, duration: 0.6, ease: "power2.out", stagger: 0.12, delay: 0.2,
      });

      // 3) ScrollTrigger — kaydırdıkça beliren bölümler (kendi kaydırma kabımız)
      gsap.utils.toArray("[data-anim='reveal']").forEach((card) => {
        gsap.from(card, {
          y: 48, opacity: 0, duration: 0.7, ease: "power2.out",
          scrollTrigger: { trigger: card, scroller: pageRef.current, start: "top 85%" },
        });
      });

      // 4) Mikro etkileşim — manyetik buton (üzerine gelince yaklaşır)
      gsap.utils.toArray("[data-anim='magnetic']").forEach((btn) => {
        const moveX = gsap.quickTo(btn, "x", { duration: 0.3, ease: "power2.out" });
        const moveY = gsap.quickTo(btn, "y", { duration: 0.3, ease: "power2.out" });
        btn.addEventListener("mousemove", (e) => {
          const r = btn.getBoundingClientRect();
          moveX((e.clientX - r.left - r.width / 2) * 0.35);
          moveY((e.clientY - r.top - r.height / 2) * 0.35);
        });
        btn.addEventListener("mouseleave", () => { moveX(0); moveY(0); });
      });
    });
    return () => mm.revert();
  }, { scope: pageRef });

  return (
    <div ref={pageRef} className="h-full overflow-y-auto zen-hc-workspace" data-testid="animasyon-demo-page">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-12">

        {/* ── HERO ─────────────────────────────────────────────────────── */}
        <header className="text-center space-y-4 py-6">
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-[#E5322A]">Yerel Demo · Kurulu Araçlar</div>
          <h1 data-anim="hero-title" className="text-4xl sm:text-5xl font-black text-slate-900 dark:text-white leading-tight">
            Animasyon Merkezi
          </h1>
          <p data-anim="hero-sub" className="text-sm text-slate-500 dark:text-slate-400 max-w-xl mx-auto">
            GSAP (Animate Anything) · Lenis · React Bits — üçü de kuruldu ve çalışıyor.
            Aşağıdaki her bölüm, programın bir yerine uygulanabilecek bir efekti gösteriyor.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
            <button onClick={toggleSmooth} className="zen-btn-primary !text-xs" data-testid="lenis-toggle">
              Pürüzsüz Kaydırma: {smoothOn ? "AÇIK" : "KAPALI"}
            </button>
            <span className="text-[11px] text-slate-400">Sayfayı kaydırarak farkı hissedin ↓</span>
          </div>
        </header>

        {/* ── 1) DASHBOARD SAYAÇLARI (React Bits CountUp + GSAP stagger) ── */}
        <section className="space-y-4">
          <h2 className="text-lg font-black text-slate-900 dark:text-white">1 · Dashboard Sayaçları</h2>
          <p className="text-xs text-slate-500">Rakamlar sıfırdan değerine sayar (React Bits <b>CountUp</b>), kartlar sırayla belirir (GSAP).</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {STATS.map((s, i) => (
              <div key={i} data-anim="stat-card" className="zen-panel p-5 rounded-2xl">
                <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">{s.label}</div>
                <div className="text-2xl font-black text-slate-900 dark:text-white">
                  <CountUp to={s.value} from={0} duration={1.6} delay={0.2 + i * 0.12}
                    prefix={s.prefix || ""} suffix={s.suffix || ""} decimals={s.decimals ?? 0}
                    separator={s.separator || ""} />
                </div>
                <div className="text-[10px] text-slate-400 mt-2 italic">{s.note}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 2) SCROLLTRIGGER — beliren bölümler ───────────────────────── */}
        <section className="space-y-4">
          <h2 className="text-lg font-black text-slate-900 dark:text-white">2 · Kaydırdıkça Beliren Bölümler</h2>
          <p className="text-xs text-slate-500">GSAP <b>ScrollTrigger</b> — her kart ekrana girince yumuşakça belirir.</p>
          <div className="grid md:grid-cols-3 gap-4">
            {REVEALS.map((r, i) => (
              <div key={i} data-anim="reveal" className="zen-panel p-6 rounded-2xl">
                <div className="w-9 h-9 rounded-xl bg-[#E5322A]/10 text-[#E5322A] flex items-center justify-center font-black mb-3">{i + 1}</div>
                <div className="font-bold text-slate-900 dark:text-white mb-1.5">{r.title}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{r.desc}</div>
              </div>
            ))}
          </div>
        </section>

        {/* ── 3) MİKRO ETKİLEŞİMLER ────────────────────────────────────── */}
        <section className="space-y-4">
          <h2 className="text-lg font-black text-slate-900 dark:text-white">3 · Mikro Etkileşimler</h2>
          <p className="text-xs text-slate-500">React Bits <b>SpringCheck</b> (animasyonlu onay) ve GSAP <b>manyetik buton</b> (üzerine gelince yaklaşır).</p>
          <div className="zen-panel p-6 rounded-2xl space-y-4">
            <SpringCheck label="Sipariş onaylandı" checked={checks[0]} onChange={(v) => setChecks((c) => [v, c[1], c[2]])} />
            <SpringCheck label="Kargoya hazır" checked={checks[1]} onChange={(v) => setChecks((c) => [c[0], v, c[2]])} />
            <SpringCheck label="Ödeme alındı" checked={checks[2]} onChange={(v) => setChecks((c) => [c[0], c[1], v])} />
          </div>
          <div className="flex justify-center pt-2">
            <button data-anim="magnetic" className="zen-btn-primary px-8 py-3 text-sm font-bold rounded-full">
              Üzerime gelin — size yaklaşırım
            </button>
          </div>
        </section>

        {/* ── 4) PROGRAMA NELER KATABİLİRİZ ────────────────────────────── */}
        <section className="space-y-4 pb-8">
          <h2 className="text-lg font-black text-slate-900 dark:text-white">4 · Programa Neler Katabiliriz?</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {ADDS.map((a, i) => (
              <div key={i} className="zen-panel p-5 rounded-2xl border-l-4 border-[#E5322A]">
                <div className="font-bold text-slate-900 dark:text-white mb-1">{a.title}</div>
                <div className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{a.desc}</div>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-slate-400 text-center pt-4">
            Beğendiğiniz efekti söyleyin — gerçek ekrana (Dashboard, Giriş, Siparişler…) ölçerek uygulayalım.
          </p>
        </section>
      </div>
    </div>
  );
}
